import { BaseInputProps } from '@paytrace/pt-skel/Component'
import { asStr } from '@paytrace/pt-skel/Helper'
import React, { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react'
import { InfoIconWithOptionalRequiredSymbol } from '../InfoIconWithOptionalRequiredSymbol'
import { InlineValidationInputText } from './InlineValidationInputText'
import { DataWithKey, TextFieldDefinition } from './Types'

interface EDInputTextProp extends BaseInputProps {
  fieldDefinition: DataWithKey<TextFieldDefinition>
  onInputChange: (fieldName: string, fieldValue: any) => void
  onInputError?: (fieldName: string, errorMessage: string | null) => void
  separator?: string
  onProductFormError?: (fieldName: string, errorMessage: string | null) => void
  onSummaryInputError?: (fieldName: string, errorMessage: string | null) => void
  runValidationNow: boolean
}

export default function EDInputText ({ value = '', onInputChange, fieldDefinition, ...props }: PropsWithChildren<EDInputTextProp>): React.JSX.Element {
  const { children, prefixInput, separator, className, disabled, readOnly = false, runValidationNow, ...callbacks } = props
  const { onProductFormError, onSummaryInputError, onInputError } = callbacks
  const { label, tooltip, name, key, showRequired, minLength, maxLength, pattern, errorMessage = '' } = fieldDefinition

  // Ref to track active focus state on this input
  const isFocusedRef = useRef(false)

  // Helper function to format input values consistently
  const formatValue = useCallback((val: any) => {
    return fieldDefinition.dataType?.formatter ? fieldDefinition.dataType.formatter(val) : val
  }, [fieldDefinition.dataType])

  // Helper function to convert visual/display value to consumable parent state (e.g. '5.00' into 5)
  const getConsumableValue = useCallback((val: any) => {
    return fieldDefinition.dataType?.consumerValueMapper ? fieldDefinition.dataType.consumerValueMapper(val) : val
  }, [fieldDefinition])

  // Initialize display state with formatted value
  const [localValue, setLocalValue] = useState(() => formatValue(value))

  // Sync localValue ONLY when value changes externally (not from local typing)
  useEffect(() => {
    if (isFocusedRef.current) {
      return // Ignore prop updates triggered by active typing
    }
    setLocalValue(formatValue(value))
  }, [value, formatValue])

  // Handle Focus: Mark active focus
  const handleOnFocus = useCallback(() => {
    isFocusedRef.current = true
  }, [])

  // Handle typing - Immediately sync parent on every character while keeping raw typed text locally
  const handleTextInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { name: inputName, value: inputValue } = event.target
    const consumableValue = getConsumableValue(inputValue)

    // Store raw typed text locally so characters like "." or trailing zeros aren't wiped mid-keystroke
    setLocalValue(inputValue)

    // Instantly notify parent of consumable value on every keystroke
    onInputChange(inputName, consumableValue)
  }, [getConsumableValue, onInputChange])

  // Handle Blur: Apply display formatting on click-out and sync parent with final formatted consumable value
  const handleOnBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    isFocusedRef.current = false

    const currentValue = e.target.value
    const formattedValue = formatValue(currentValue)
    const consumableValue = getConsumableValue(formattedValue)

    // Update local display with formatted value (e.g. "5." -> "5.00")
    setLocalValue(formattedValue)

    // Send final consumable value to parent state
    if (onInputChange) {
      onInputChange(name, consumableValue)
    }
  }, [formatValue, getConsumableValue, name, onInputChange])

  const layoutClass = className || 'enhanced-data-input'

  const handleValidationError = useCallback((message: any) => {
    if (onProductFormError) {
      onProductFormError(name, message || null)
    }
    if (onSummaryInputError) {
      onSummaryInputError(name, message || null)
    }
    if (onInputError) {
      onInputError(name, message || null)
    }
  }, [name, onInputError, onProductFormError, onSummaryInputError])

  const labelIconWithSeparator = (
    <span className="label-icon-wrapper">
      <InfoIconWithOptionalRequiredSymbol
        message={tooltip}
        className={'hint'}
        onHover={true}
        showRequiredSymbol={showRequired}
      />
      {separator && <span className="input-separator">{separator}</span>}
    </span>
  )

  const formattedPrefix = prefixInput ? <span className="padded-prefix">{prefixInput}</span> : undefined

  return (
    <div>
      <InlineValidationInputText
        required={showRequired}
        className={layoutClass}
        label={label}
        name={name}
        id={asStr(key)}
        value={localValue}
        labelIcon={labelIconWithSeparator}
        onChange={handleTextInputChange}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        maxLength={maxLength}
        minLength={minLength}
        pattern={pattern}
        prefixInput={formattedPrefix}
        disabled={disabled}
        readOnly={readOnly}
        fieldErrorMessage={errorMessage}
        onValidationError={handleValidationError}
        runValidationNow={runValidationNow}
      />
      {children}
    </div>
  )
}
